import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaCloudUploadAlt, FaImage, FaSearch, FaTimes } from 'react-icons/fa';
import { api } from '../api';

const ACCEPTED_TYPES = 'image/jpeg,image/jpg,image/png,image/svg+xml,image/webp';

const MediaPickerField = ({ label, value, onChange, required = false }) => {
  const inputRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [showLibrary, setShowLibrary] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    if (!showLibrary) return;

    let isMounted = true;
    const loadMedia = async () => {
      try {
        setLoading(true);
        setLoadError('');
        const response = await api.list('/api/media-library');
        if (isMounted) {
          setItems(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        if (isMounted) {
          setLoadError(err.message || 'Failed to load media library.');
          setItems([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadMedia();
    return () => {
      isMounted = false;
    };
  }, [showLibrary]);

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return items;
    return items.filter((item) => {
      const fileName = String(item.fileName || '').toLowerCase();
      const fileUrl = String(item.fileUrl || '').toLowerCase();
      return fileName.includes(query) || fileUrl.includes(query);
    });
  }, [items, search]);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError('');
    try {
      const uploaded = await api.uploadImage(file);
      onChange(uploaded.fileUrl);
    } catch (err) {
      setUploadError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const clearImage = () => onChange('');

  return (
    <div>
      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-gray-400">
        {label}
        {required ? ' *' : ''}
      </label>

      {value ? (
        <div className="mb-2 overflow-hidden rounded-xl border border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-[#0d1525]">
          <img src={value} alt={label} className="h-28 w-full object-cover" />
          <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-3 py-2 dark:border-white/10">
            <p className="truncate text-xs text-slate-500 dark:text-gray-400">{value}</p>
            <button
              type="button"
              onClick={clearImage}
              className="rounded-md border border-red-500/40 px-2 py-1 text-xs font-semibold text-red-500 hover:bg-red-500/10 dark:text-red-400"
            >
              <FaTimes size={10} className="inline mr-1" />
              Remove
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-2 flex h-24 items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 text-slate-400 dark:border-white/20 dark:bg-[#0d1525] dark:text-gray-500">
          <div className="text-center">
            <FaImage className="mx-auto mb-1 text-lg" />
            <p className="text-xs">No media selected</p>
          </div>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        className="hidden"
        onChange={handleFileSelect}
      />

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm font-bold text-primary hover:bg-primary/20 disabled:opacity-60"
        >
          <FaCloudUploadAlt size={14} />
          {uploading ? 'Uploading...' : 'Upload Image'}
        </button>
        <button
          type="button"
          onClick={() => setShowLibrary((prev) => !prev)}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-bold text-slate-700 hover:border-primary/40 hover:text-primary dark:border-white/15 dark:text-white"
        >
          <FaSearch size={13} />
          {showLibrary ? 'Hide Media Library' : 'Browse Media Library'}
        </button>
      </div>

      {uploadError ? <p className="mt-1 text-xs text-red-500 dark:text-red-400">{uploadError}</p> : null}

      {showLibrary ? (
        <div className="mt-3 rounded-xl border border-slate-200 p-3 dark:border-white/10">
          <div className="relative mb-3">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={12} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search media by name or URL..."
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 focus:border-primary focus:outline-none dark:border-white/15 dark:bg-[#1a1a1a] dark:text-white"
            />
          </div>

          {loading ? (
            <p className="text-xs text-slate-500 dark:text-gray-400">Loading media...</p>
          ) : loadError ? (
            <p className="text-xs text-red-500 dark:text-red-400">{loadError}</p>
          ) : filteredItems.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-gray-400">No media files found.</p>
          ) : (
            <div className="grid max-h-56 grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4">
              {filteredItems.map((item) => {
                const isActive = value === item.fileUrl;
                return (
                  <button
                    key={item.id || item.fileUrl}
                    type="button"
                    onClick={() => onChange(item.fileUrl || '')}
                    title={item.fileName || item.fileUrl}
                    className={`overflow-hidden rounded-lg border transition-colors ${
                      isActive
                        ? 'border-primary ring-2 ring-primary/30'
                        : 'border-slate-200 hover:border-primary/40 dark:border-white/10'
                    }`}
                  >
                    <img
                      src={item.fileUrl}
                      alt={item.fileName || 'Media'}
                      className="h-16 w-full object-cover"
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : null}

      <p className="mt-1 text-[11px] text-slate-400 dark:text-gray-500">
        Upload a new image or pick one from the media library.
      </p>
    </div>
  );
};

export default MediaPickerField;
